import { PieChartResponseDto } from "@/lib/interfaces/dashboard";
import { useMemo } from "react";
import { Label, Pie, PieChart as RechartsPieChart } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "./chart";

const COLORS = [
  "hsl(var(--chart-2))",
  "hsl(var(--chart-1))",
  "hsl(var(--chart-3, 215 50% 23%))",
  "hsl(var(--chart-4, 142 76% 36%))",
  "hsl(var(--chart-5, 346 77% 49%))",
];

interface PieDataItem {
  name: string;
  value: number;
  fill: string;
}

const PieChart = ({ title, description, data }: { title: string; description: string; data?: PieChartResponseDto }) => {
  // Transform data for pie chart format
  const pieData = useMemo(
    () =>
      data?.elements.map((item, index) => ({
        name: item.name,
        value: item.value,
        fill: COLORS[index % COLORS.length],
      })) || [],
    [data]
  );

  // Calculate total value for center label
  const totalValue = useMemo(() => pieData.reduce((acc: number, curr: PieDataItem) => acc + curr.value, 0), [pieData]);

  if (!data) return null;

  // Dynamically create chart config
  const chartConfig = pieData.reduce(
    (config, item, index) => ({
      ...config,
      [item.name]: {
        label: item.name,
        color: COLORS[index % COLORS.length],
      },
    }),
    {}
  ) satisfies ChartConfig;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[250px]">
          {totalValue > 0 ? (
            <RechartsPieChart>
              <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={60} strokeWidth={5}>
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                      return (
                        <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                          <tspan x={viewBox.cx} y={viewBox.cy} className="fill-foreground text-3xl font-bold">
                            {totalValue.toLocaleString()}
                          </tspan>
                          <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 24} className="fill-muted-foreground">
                            Total
                          </tspan>
                        </text>
                      );
                    }
                  }}
                />
              </Pie>
            </RechartsPieChart>
          ) : (
            <div className="flex justify-center items-center h-full">
              <span className="text-muted-foreground">Keine Daten</span>
            </div>
          )}
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

export default PieChart;
